import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { api, euro, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as DayCalendar } from "@/components/ui/calendar";
import { StarRating } from "@/components/StarRating";
import { ImageUploader } from "@/components/ImageUploader";
import { Plus, Trash2, Loader2, Power, Calendar, Users, Pencil, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CATS = [
  ["pizzawagen", "Pizzawagen"], ["crepestand", "Crêpestand"], ["bratwurststand", "Bratwurststand"],
  ["dj", "DJ"], ["hochzeitssaenger", "Hochzeitssänger"], ["cocktailbar", "Cocktailbar"],
  ["fotobox", "Fotobox"], ["bbq", "BBQ Foodtruck"],
];
const BSTATUS = { pending: ["In Bearbeitung", "bg-amber-100 text-amber-700"], confirmed: ["Bestätigt", "bg-emerald-100 text-emerald-700"], declined: ["Abgelehnt", "bg-red-100 text-red-700"], completed: ["Abgeschlossen", "bg-slate-200 text-slate-700"] };

const empty = { title: "", category: "pizzawagen", description: "", base_price: 800, base_persons: 50, base_hours: 3, extra_person_price: 12, extra_hour_price: 150, city: "", image_url: "", service_radius_km: 50 };

export default function PartnerDashboard() {
  const [services, setServices] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [addonsText, setAddonsText] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [replyText, setReplyText] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [availOpen, setAvailOpen] = useState(false);
  const [availService, setAvailService] = useState(null);
  const [availDates, setAvailDates] = useState([]);

  const load = () => {
    api.get("/partner/services").then((r) => setServices(r.data));
    api.get("/partner/bookings").then((r) => setBookings(r.data));
    api.get("/partner/reviews").then((r) => setReviews(r.data));
  };
  useEffect(load, []);

  const num = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });
  const str = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const addons = addonsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [name, price] = l.split("|");
        return { name: (name || "").trim(), price: Number((price || "0").trim()) };
      }).filter((a) => a.name);
      const cover = galleryImages[0] || "";
      const images = galleryImages.slice(1);
      const payload = { ...form, image_url: cover, addons, images };
      if (editingId) {
        await api.put(`/services/${editingId}`, payload);
        toast.success("Angebot aktualisiert!");
      } else {
        await api.post("/services", payload);
        toast.success("Angebot erstellt!");
      }
      setOpen(false); setForm(empty); setAddonsText(""); setGalleryImages([]); setEditingId(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const openNew = () => { setEditingId(null); setForm(empty); setAddonsText(""); setGalleryImages([]); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ title: s.title, category: s.category, description: s.description, base_price: s.base_price,
      base_persons: s.base_persons, base_hours: s.base_hours, extra_person_price: s.extra_person_price,
      extra_hour_price: s.extra_hour_price, city: s.city, image_url: s.image_url, service_radius_km: s.service_radius_km });
    setAddonsText((s.addons || []).map((a) => `${a.name} | ${a.price}`).join("\n"));
    setGalleryImages([s.image_url, ...(s.images || [])].filter(Boolean));
    setOpen(true);
  };
  const openAvail = async (s) => {
    setAvailService(s);
    const { data } = await api.get(`/services/${s.id}/availability`);
    setAvailDates(data.unavailable_dates || []);
    setAvailOpen(true);
  };
  const saveAvail = async () => {
    await api.patch(`/services/${availService.id}/availability`, { unavailable_dates: availDates });
    toast.success("Verfügbarkeit gespeichert"); setAvailOpen(false); load();
  };

  const del = async (id) => { await api.delete(`/services/${id}`); toast.success("Gelöscht"); load(); };
  const toggle = async (id) => { await api.patch(`/services/${id}/toggle`); load(); };
  const setStatus = async (id, status) => { await api.patch(`/bookings/${id}/status`, { status }); toast.success("Aktualisiert"); load(); };
  const sendReply = async (id) => { await api.patch(`/reviews/${id}/reply`, { reply: replyText[id] || "" }); toast.success("Antwort gespeichert"); load(); };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">Partner-Dashboard</h1>
            <p className="text-slate-500">Verwalte deine Angebote und Buchungsanfragen</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={openNew} data-testid="new-service-btn"><Plus className="w-4 h-4" /> Angebot erstellen</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-form-dialog">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editingId ? "Angebot bearbeiten" : "Neues Angebot"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titel</Label><Input data-testid="sf-title" value={form.title} onChange={str("title")} placeholder="z.B. Mobiler Pizzawagen" /></div>
                <div><Label>Kategorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="sf-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Beschreibung</Label><Textarea data-testid="sf-desc" value={form.description} onChange={str("description")} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Basispreis (€)</Label><Input data-testid="sf-price" type="number" value={form.base_price} onChange={num("base_price")} /></div>
                  <div><Label>Stadt</Label><Input data-testid="sf-city" value={form.city} onChange={str("city")} /></div>
                  <div><Label>Basis Personen</Label><Input type="number" value={form.base_persons} onChange={num("base_persons")} /></div>
                  <div><Label>Basis Stunden</Label><Input type="number" value={form.base_hours} onChange={num("base_hours")} /></div>
                  <div><Label>€ / weitere Person</Label><Input type="number" value={form.extra_person_price} onChange={num("extra_person_price")} /></div>
                  <div><Label>€ / weitere Stunde</Label><Input type="number" value={form.extra_hour_price} onChange={num("extra_hour_price")} /></div>
                </div>
                <div><Label>Bilder (erstes Bild = Titelbild)</Label><ImageUploader images={galleryImages} onChange={setGalleryImages} /></div>
                <div><Label>Extras (je Zeile: Name | Preis)</Label><Textarea value={addonsText} onChange={(e) => setAddonsText(e.target.value)} placeholder={"Vegane Option | 150\nDessert-Station | 200"} /></div>
                <Button onClick={save} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="sf-submit">{saving ? "Speichern…" : editingId ? "Änderungen speichern" : "Angebot veröffentlichen"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="offers">
          <TabsList><TabsTrigger value="offers" data-testid="tab-offers">Meine Angebote</TabsTrigger><TabsTrigger value="bookings" data-testid="tab-bookings">Buchungsanfragen ({bookings.length})</TabsTrigger><TabsTrigger value="reviews" data-testid="tab-reviews">Bewertungen ({reviews.length})</TabsTrigger></TabsList>

          <TabsContent value="offers" className="mt-6">
            {services === null ? <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" /> :
              services.length === 0 ? <div className="text-center py-16 text-slate-500">Noch keine Angebote. Erstelle dein erstes Angebot!</div> :
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-services">
                {services.map((s) => (
                  <div key={s.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                    <img src={s.image_url} alt="" className="w-full h-36 object-cover" />
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-slate-900">{s.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{s.active ? "Aktiv" : "Inaktiv"}</span>
                      </div>
                      <div className="font-mono font-bold text-emerald-700 mt-1">{euro(s.base_price)} · {s.base_persons} Pers.</div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /> Bearbeiten</Button>
                        <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => openAvail(s)} data-testid={`avail-${s.id}`}><CalendarDays className="w-3.5 h-3.5" /> Kalender</Button>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => toggle(s.id)} data-testid={`toggle-${s.id}`}><Power className="w-3.5 h-3.5" /> {s.active ? "Deaktiv." : "Aktiv."}</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => del(s.id)} data-testid={`delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            {bookings.length === 0 ? <div className="text-center py-16 text-slate-500">Noch keine Buchungsanfragen.</div> :
              <div className="space-y-3" data-testid="partner-bookings">
                {bookings.map((b) => (
                  <div key={b.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-slate-400 font-mono">{b.reference}</div>
                        <h3 className="font-bold text-slate-900">{b.service_title}</h3>
                        <p className="text-sm text-slate-500">{b.customer_name} · {b.contact_phone}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {b.event_date} {b.event_time}</span>
                          <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {b.guests}</span>
                          <span>{b.city}</span>
                        </div>
                        {b.special_requests && <p className="text-sm text-slate-500 mt-2 italic">„{b.special_requests}“</p>}
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-extrabold text-lg text-emerald-700">{euro(b.total_amount)}</div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${BSTATUS[b.booking_status][1]}`}>{BSTATUS[b.booking_status][0]}</span>
                      </div>
                    </div>
                    {b.booking_status === "pending" && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}>Annehmen</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setStatus(b.id, "declined")} data-testid={`decline-${b.id}`}>Ablehnen</Button>
                      </div>
                    )}
                    {b.booking_status === "confirmed" && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "completed")} data-testid={`complete-${b.id}`}>Als abgeschlossen markieren</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>}
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            {reviews.length === 0 ? <div className="text-center py-16 text-slate-500">Noch keine Bewertungen.</div> :
              <div className="space-y-3" data-testid="partner-reviews">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">{r.customer_name}</div>
                        <div className="text-xs text-slate-500">{r.service_title}</div>
                      </div>
                      <StarRating value={r.rating} readOnly starClass="w-4 h-4" />
                    </div>
                    {r.comment && <p className="text-slate-600 text-sm mt-2">{r.comment}</p>}
                    {r.reply ? (
                      <div className="mt-3 pl-3 border-l-2 border-emerald-300 bg-emerald-50/60 p-2.5 rounded-r-lg">
                        <div className="text-xs font-semibold text-emerald-700">Deine Antwort</div>
                        <p className="text-sm text-slate-600 mt-0.5">{r.reply}</p>
                      </div>
                    ) : (
                      <div className="mt-3 flex gap-2">
                        <Input placeholder="Öffentlich antworten…" data-testid={`reply-input-${r.id}`}
                          value={replyText[r.id] || ""} onChange={(e) => setReplyText({ ...replyText, [r.id]: e.target.value })} />
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0" onClick={() => sendReply(r.id)} data-testid={`reply-send-${r.id}`}>Senden</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>}
          </TabsContent>
        </Tabs>

        <Dialog open={availOpen} onOpenChange={setAvailOpen}>
          <DialogContent className="sm:max-w-fit" data-testid="availability-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Verfügbarkeit · {availService?.title}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500 -mt-2 max-w-xs">Wähle Tage aus, an denen du <strong>nicht</strong> verfügbar bist. Diese Tage werden für Kunden gesperrt.</p>
            <DayCalendar mode="multiple" selected={availDates.map((d) => new Date(d))} onSelect={(ds) => setAvailDates((ds || []).map(toISO))} className="rounded-md border" />
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">{availDates.length} Tag(e) gesperrt</span>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveAvail} data-testid="save-availability">Speichern</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
